import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { getLYDateFor, compareDateByMonth } from '../../helpers/dateHelper';
import {
    mergeLastYearData,
    calculateTotals,
    mergeStats,
    formatIntoCalendarViewFormat,
    groupByMonthAndAddMissingForecast,
} from './parser';
import { getDateRanges } from './utils';
import { FEATURES } from 'utils/permissions';
// import { getToday, isPast } from 'utils/dates';
import { getToday, isPast, format } from 'utils/dayjsUtils';

import session from 'containers/App/session';
import forecastModule from 'containers/App/data/endpoints/forecastByDay';
import roomsRevModule from 'containers/App/data/endpoints/currentRoomsRevenueByDay';
import budgetByMonthModule from 'containers/App/data/endpoints/budgetByMonth';
import lySpitRoomsRevenueByMonthModule from 'containers/App/data/endpoints/lySpitRoomsRevenueByMonth';

import CalendarView from './calendarView';
import dayjs from 'utils/dayjs';
import { isEmpty } from 'lodash';
import { Skeleton } from 'antd';

const YearView = () => {
    const propertyId = useSelector(session.selectors.selectSelectedPropertyId);
    const currency = useSelector(session.selectors.selectCurrency);
    const financialYearStart = useSelector(session.selectors.selectFinancialYearStart) || '0101';
    const accessibleFeatures = useSelector(session.selectors.selectAccessibleFeatures);
    const forecast = useSelector(forecastModule.selectors.selectState);
    const roomsRevenueByDay = useSelector(roomsRevModule.selectors.selectState);
    const budgetByMonth = useSelector(budgetByMonthModule.selectors.selectState);
    const lySpitRoomsRevenueByMonth = useSelector(lySpitRoomsRevenueByMonthModule.selectors.selectState);

    const dispatch = useDispatch();
    const navigate = useNavigate();

    const yearStartMonth = financialYearStart.substring(0, 2);
    const propertyYearStart = `${yearStartMonth > dayjs().month() + 1 ? dayjs().subtract(1, 'year').year() : dayjs().year()}-${yearStartMonth}-${financialYearStart.substring(2, 4)}`;
    const [startDate, setStartDate] = useState(dayjs(propertyYearStart, 'YYYY-MM-DD'));
    const [totals, setTotals] = useState([]);
    const [statsByMonth, setStatsByMonth] = useState([]);

    const dateDisplayFormat = 'MMMM YYYY';

    const parseData = () => {
        const parsingOptions = {
            dateFormat: 'YYYYMM',
            precision: 'month',
            getLySpitDate: getLYDateFor,
        };
        const stats = groupByMonthAndAddMissingForecast(roomsRevenueByDay.data, forecast.data || []);
        const data = mergeStats(stats.data, budgetByMonth.data, stats.forecast);
        const parsedData = formatIntoCalendarViewFormat(data, parsingOptions);
        const parsedLySpit = formatIntoCalendarViewFormat(lySpitRoomsRevenueByMonth.data, parsingOptions);
        const mergedData = mergeLastYearData(parsedData, parsedLySpit, parsingOptions);
        const calculatedTotals = calculateTotals(mergedData);
        setStatsByMonth(mergedData);
        setTotals(calculatedTotals);
    };
    const getApiData = () => {
        dispatch(roomsRevModule.actions.resetState());
        dispatch(budgetByMonthModule.actions.resetState());
        dispatch(lySpitRoomsRevenueByMonthModule.actions.resetState());
        dispatch(forecastModule.actions.resetState());

        const { startDate: start, endDate: end, lyStartDate, lyEndDate } = getDateRanges(startDate, 'year');
        const startFormatted = start.format('YYYY-MM-DD');
        const endFormatted = end.format('YYYY-MM-DD');
        dispatch(roomsRevModule.actions.fetch(propertyId, startFormatted, endFormatted));
        dispatch(budgetByMonthModule.actions.fetch(propertyId, startFormatted, endFormatted));
        dispatch(
            lySpitRoomsRevenueByMonthModule.actions.fetch(
                propertyId,
                lyStartDate.format('YYYY-MM-DD'),
                lyEndDate.format('YYYY-MM-DD'),
            ),
        );

        if (!isPast(end)) {
            const forecastStart = isPast(start) ? getToday() : format(start);
            dispatch(forecastModule.actions.fetch(propertyId, forecastStart, format(end)));
        }
    };

    const onChangeDate = (month, year) => {
        setStartDate(dayjs(`${year}-${String(month).padStart(2, '0')}-01`, 'YYYY-MM-DD'));
    };

    const onClickCard = (date) => {
        navigate('/app/monthly', {
            state: {
                selectedDate: date.format('YYYY-MM-DD'),
            },
        });
    };

    useEffect(() => {
        getApiData();
        return () => {
            // on unmount
            dispatch(roomsRevModule.actions.resetState());
            dispatch(budgetByMonthModule.actions.resetState());
            dispatch(lySpitRoomsRevenueByMonthModule.actions.resetState());
            dispatch(forecastModule.actions.resetState());
            setTotals([]);
            setStatsByMonth([]);
        };
    }, [propertyId, startDate]);

    const isLoading =
        roomsRevenueByDay.fetching ||
        forecast.fetching ||
        budgetByMonth.fetching ||
        lySpitRoomsRevenueByMonth.fetching ||
        !roomsRevenueByDay.fetched ||
        (!isPast(getDateRanges(startDate, 'year').endDate) && !forecast.fetched) ||
        !budgetByMonth.fetched ||
        !lySpitRoomsRevenueByMonth.fetched;

    useEffect(() => {
        if (!isLoading) {
            parseData();
        }
    }, [isLoading]);

    return (
        <>
            {isLoading && (
                <>
                    <Skeleton active />
                    <Skeleton active />
                    <Skeleton active />
                </>)
            }
            {!isLoading && !isEmpty(totals) && (
                <CalendarView
                    currency={currency}
                    selectedMonth={startDate.month() + 1}
                    selectedYear={startDate.year()}
                    dateChanged={onChangeDate}
                    isLoading={isLoading}
                    totals={totals}
                    cards={statsByMonth}
                    showForecast={accessibleFeatures.includes(FEATURES.revenueForecast)}
                    cardsProps={{
                        dateDisplayFormat,
                        cardClicked: onClickCard,
                        compareDates: compareDateByMonth,
                        showChange: false,
                        showBudget: true,
                    }}
                />
            )}
        </>
    );
};

export default YearView;
